import React, { useCallback } from 'react';
import { Alert, Card, Spin, Typography } from 'antd';
import { useClosingPackageProjection } from '../hooks/useClosingPackageProjection';
import ClosingPackageWorkflowHeader from './ClosingPackageWorkflowHeader';
import ClosingPackageSummaryCards from './ClosingPackageSummaryCards';
import ClosingPackageActiveTaskInfo from './ClosingPackageActiveTaskInfo';
import ClosingPackageCompositionEditor from './ClosingPackageCompositionEditor';
import ClosingPackageTimeline from './ClosingPackageTimeline';
import ClosingPackageJournalPanel from './ClosingPackageJournalPanel';
import './closingPackageWorkspace.css';

const { Text } = Typography;

export type ClosingPackageWorkspaceProps = {
  applicationId: number;
  /** Refresh host document list (ApplicationDocumentsTab). */
  onRefreshDocuments?: () => void;
};

/**
 * Closing Package Workspace shell (FE-002…FE-004A).
 * Informational module for package state — not a task execution surface.
 * Host: Application → Documents. Data via useClosingPackageProjection (FE-001 AR-04).
 * Composition table: Composition Editor foundation (FE-005A-1).
 * Timeline: journal-backed lifecycle history (FE-005A).
 */
const ClosingPackageWorkspace: React.FC<ClosingPackageWorkspaceProps> = ({
  applicationId,
  onRefreshDocuments,
}) => {
  const { data: projection, isLoading, isFetching, isError, error, refetch } =
    useClosingPackageProjection(applicationId);

  const handleRefresh = useCallback(() => {
    void refetch();
    onRefreshDocuments?.();
  }, [onRefreshDocuments, refetch]);

  if (isLoading) {
    return (
      <Card size="small" className="cp-workspace" style={{ marginBottom: 16 }}>
        <div className="cp-workspace__loading">
          <Spin />
          <Text type="secondary" style={{ marginTop: 12, display: 'block', textAlign: 'center' }}>
            Загрузка комплекта…
          </Text>
        </div>
      </Card>
    );
  }

  if (isError) {
    const message =
      (error as { data?: { error?: string }; message?: string })?.data?.error ||
      (error as { message?: string })?.message ||
      'Не удалось загрузить комплект закрывающих документов';
    return (
      <Card
        size="small"
        className="cp-workspace"
        title="Комплект закрывающих документов"
        style={{ marginBottom: 16 }}
      >
        <Alert type="error" showIcon message={message} />
      </Card>
    );
  }

  if (!projection || projection.workflowState === 'NOT_AVAILABLE') {
    return (
      <Card
        size="small"
        className="cp-workspace"
        title="Комплект закрывающих документов"
        style={{ marginBottom: 16 }}
      >
        <Text type="secondary">
          Комплект будет доступен после завершения последнего маршрута заявки.
        </Text>
      </Card>
    );
  }

  return (
    <Card size="small" className="cp-workspace" style={{ marginBottom: 16 }} bordered>
      <div className="cp-workspace__layout">
        <div className="cp-workspace__header-sticky">
          <ClosingPackageWorkflowHeader
            projection={projection}
            isFetching={isFetching}
            onRefresh={handleRefresh}
          />
        </div>

        <div className="cp-workspace__body">
          <ClosingPackageSummaryCards projection={projection} />
          <div className="cp-span-full">
            <ClosingPackageActiveTaskInfo applicationId={applicationId} projection={projection} />
          </div>
          <ClosingPackageCompositionEditor projection={projection} />
          <ClosingPackageTimeline applicationId={applicationId} />
          <ClosingPackageJournalPanel
            applicationId={applicationId}
            items={projection.items}
          />
        </div>
      </div>
    </Card>
  );
};

export default ClosingPackageWorkspace;
